import { Stack, Card, Box, Radio, Inline } from "@adfenix/alexandria";
import styled from "styled-components";
import { useState, ReactNode } from "react";
import { Collapse } from "react-collapse";
import { SelectableTemplate } from "./SelectableTemplate";
import { ToggleButton } from "./ToggleButton";
import { TemplateName } from "./TemplateName";
import { useFormContext } from "react-hook-form";
import { When } from "components/When/When";
import { TemplateActions } from "./TemplateActions";
import { ProgramService } from "@adfenix/types";
import { TemplateUiModel } from "routes/back-office/program-builder/details/campaign/meta/template/TemplateUiModel";
import { useDisabledFields } from "../../../useDisabledFields";
import { produce } from "immer";
import { useMetaTemplateContext } from "../../util/CampaignTemplateContext";

interface Props {
  template: TemplateUiModel;
  onPreview: (id: string) => void;
  selectableTemplates: TemplateUiModel[];
  children: ReactNode;
}

const ButtonColumn = styled(Box)`
  margin-left: auto;
`;

export const getAvailableTemplatesKey = (type: "image" | "ad") => {
  return type === "image"
    ? "availableImageTemplates"
    : "availableContentTemplates";
};

export const getSelectedTemplateKey = (type: "image" | "ad") => {
  return type === "image"
    ? "selectedImageTemplates"
    : "selectedContentTemplates";
};

export const AvailableTemplate = ({
  children,
  onPreview,
  template,
  selectableTemplates
}: Props) => {
  const disabledFields = useDisabledFields();
  const [switchActive, setSwitchActive] = useState(false);

  const { register, watch, setValue } =
    useFormContext<ProgramService.ProgramModel>();

  const { campaignKey } = useMetaTemplateContext();

  const availableTemplates =
    watch(`${campaignKey}.${getAvailableTemplatesKey(template.type)}`) ?? [];

  const selectedTemplates =
    watch(`${campaignKey}.${getSelectedTemplateKey(template.type)}`) ?? {};

  return (
    <div>
      <Card p="m" data-testid={`selected-template-${template.id}`}>
        <Stack spacing="s">
          <Box alignItems="center" display="flex">
            <Box>
              <TemplateName template={template} />
            </Box>

            <ButtonColumn>
              <TemplateActions
                template={template}
                onPreview={() => onPreview(template.id)}
                onDelete={() => {
                  setValue(
                    `${campaignKey}.${getAvailableTemplatesKey(template.type)}`,
                    availableTemplates.filter(x => x !== template.id),
                    { shouldDirty: true }
                  );

                  setValue(
                    `${campaignKey}.${getSelectedTemplateKey(template.type)}`,
                    produce(selectedTemplates, draft => {
                      Object.keys(selectedTemplates).forEach(channel => {
                        const value = draft[channel];
                        if (value === template.id || !value) {
                          delete draft[channel];
                        }
                      });
                    })
                  );
                }}
                disabled={disabledFields(
                  template.type === "ad"
                    ? "bundleGroups.bundles.campaignTemplates.availableAdTemplates"
                    : "bundleGroups.bundles.campaignTemplates.availableImageTemplates"
                )}
              />
            </ButtonColumn>
          </Box>
          {children}

          <Inline gap="m">
            {template.channels.map(channel => (
              <Radio
                readOnly={disabledFields(
                  template.type === "ad"
                    ? "bundleGroups.bundles.campaignTemplates.selectedAdTemplate"
                    : "bundleGroups.bundles.campaignTemplates.selectedAdImageTemplates"
                )}
                data-testid={`${template.name}-${channel}`}
                value={template.id}
                key={`${template.id}-${channel}`}
                id={`${template.id}-${channel}`}
                label={`Default for ${channel}`}
                {...register(
                  `${campaignKey}.${
                    template.type === "ad"
                      ? "selectedContentTemplates"
                      : "selectedImageTemplates"
                  }.${channel}`,
                  {
                    required: `Channel ${channel} has no default ${template.type} template.`
                  }
                )}
              />
            ))}
          </Inline>

          <When truthy={selectableTemplates.length > 0}>
            <ToggleButton
              isToggled={switchActive}
              setToggle={setSwitchActive}
              toggleMessage="Switch"
              toggledMessage="Cancel"
              disabled={disabledFields(
                template.type === "ad"
                  ? "bundleGroups.bundles.campaignTemplates.availableAdTemplates"
                  : "bundleGroups.bundles.campaignTemplates.availableImageTemplates"
              )}
            />
          </When>
        </Stack>
      </Card>

      <Collapse isOpened={switchActive}>
        {selectableTemplates.map(x => (
          <SelectableTemplate
            disabled={disabledFields(
              template.type === "ad"
                ? "bundleGroups.bundles.campaignTemplates.availableAdTemplates"
                : "bundleGroups.bundles.campaignTemplates.availableImageTemplates"
            )}
            template={x}
            key={x.id}
            mode="switch"
            onSelect={switchTarget => {
              setSwitchActive(false);
              setValue(
                `${campaignKey}.${getAvailableTemplatesKey(template.type)}`,
                produce(availableTemplates, draft => {
                  const index = draft.findIndex(x => x === template.id);
                  draft[index] = switchTarget.id;
                })
              );
            }}
          />
        ))}
      </Collapse>
    </div>
  );
};
